import React, { useContext, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Flex,
  IconButton,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { ethnicityOptions } from 'types';
import { influencerApi } from 'services/api/influencer';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { formatPhoneNumber } from 'utils/authUtils';
import { parsePhone } from '../utils';
import { ff } from '../../../theme';

interface PersonalInformationEditProps {
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  email_address: string;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  countryCode: string;
  setCountryCode: (value: string) => void;
  birthday: string;
  setBirthday: (value: string) => void;
  gender: string;
  setGender: (value: string) => void;
  pronouns: { value: string; label: string }[];
  setPronouns: (value: any) => void;
  ethnicity: string;
  setEthnicity: (value: string) => void;
  languages: string[];
  setLanguages: (value: string[]) => void;
  lastSavedPersonalInfo: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    countryCode: string;
    birthday: string;
    gender: string;
    pronouns: { value: string; label: string }[];
    ethnicity: string;
    languages: string[];
  } | null;
  onFieldSaved: (fieldKey: string, value: any) => void;
  onBack: () => void;
  /** Candidate address the creator is typing. Separate from the live one on purpose. */
  newEmail: string;
  setNewEmail: (value: string) => void;
  /** Set while a verification link is outstanding. */
  pendingEmail?: string;
  /** Cancelled, but still reserved: a sent verification link cannot be revoked. */
  pendingEmailCancelled?: boolean;
  /** False while staff are switched into this creator: the link would move the staff login, not the creator's. */
  canChangeEmail?: boolean;
  isRequestingEmailChange: boolean;
  onRequestEmailChange: () => void;
  onCancelEmailChange: () => void;
}

const pronounOptions = [
  { value: 'She/Her', label: 'She/Her' },
  { value: 'He/Him', label: 'He/Him' },
  { value: 'They/Them', label: 'They/Them' },
  { value: 'Other', label: 'Other' },
];

const languageOptions = [
  'English',
  'Spanish',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Russian',
  'Japanese',
  'Korean',
  'Chinese (Mandarin)',
  'Chinese (Cantonese)',
  'Arabic',
  'Hindi',
  'Bengali',
  'Punjabi',
  'Urdu',
  'Vietnamese',
  'Tagalog',
  'Thai',
  'Turkish',
  'Polish',
  'Dutch',
  'Greek',
  'Hebrew',
  'Swedish',
  'Norwegian',
  'Danish',
  'Finnish',
  'Other',
];

const PersonalInformationEdit: React.FC<PersonalInformationEditProps> = ({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email_address,
  newEmail,
  setNewEmail,
  pendingEmail,
  pendingEmailCancelled,
  canChangeEmail = true,
  isRequestingEmailChange,
  onRequestEmailChange,
  onCancelEmailChange,
  phoneNumber,
  setPhoneNumber,
  countryCode,
  setCountryCode,
  birthday,
  setBirthday,
  gender,
  setGender,
  pronouns,
  setPronouns,
  ethnicity,
  setEthnicity,
  languages,
  setLanguages,
  lastSavedPersonalInfo,
  onFieldSaved,
  onBack,
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const [showLanguageInput, setShowLanguageInput] = React.useState(false);
  const [languageInput, setLanguageInput] = React.useState('');
  const [isSaving, setIsSaving] = useState(false);
  const selectRef = React.useRef<HTMLSelectElement>(null);
  
  // Per-field saving state flags
  const [isSavingFirstName, setIsSavingFirstName] = useState(false);
  const [isSavingLastName, setIsSavingLastName] = useState(false);
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [isSavingBirthday, setIsSavingBirthday] = useState(false);
  const [isSavingGender, setIsSavingGender] = useState(false);
  const [isSavingPronouns, setIsSavingPronouns] = useState(false);
  const [isSavingEthnicity, setIsSavingEthnicity] = useState(false);
  const [isSavingLanguages, setIsSavingLanguages] = useState(false);

  /**
   * Generic autosave helper function
   * 
   * This pattern can be reused in other profile edit components (NichesEdit, SocialsEdit, etc.)
   * 
   * Usage:
   * 1. Call autosaveField with the field key, payload builder, saving flag setter, revert function, and display name
   * 2. The function will:
   *    - Set the saving flag to show loading indicator
   *    - Build and send minimal payload to Firebase
   *    - On success: update lastSavedPersonalInfo via onFieldSaved callback
   *    - On error: revert field to last saved value and show error toast
   *    - Clear saving flag when done
   * 
   * For text inputs: trigger on blur or Enter key
   * For select/multi-select: trigger immediately on change
   */
  const autosaveField = async (
    fieldKey: string,
    buildPayloadFn: () => any,
    setSavingFlag: (value: boolean) => void,
    applyRevertFn: () => void,
    fieldDisplayName: string
  ) => {
    if (!lastSavedPersonalInfo || !influencerData?.id) return;
    
    try {
      setSavingFlag(true);
      const payload = {
        ...buildPayloadFn(),
      };
      
      await influencerApi.updateProfile(payload, influencerData.id);
      
      // On success, update last saved value
      let currentValue: any = null;
      if (fieldKey === 'phoneNumber') {
        currentValue = formatPhoneNumber(phoneNumber, countryCode);
      } else if (fieldKey === 'pronouns') {
        currentValue = pronouns;
      } else if (fieldKey === 'firstName') {
        currentValue = firstName;
      } else if (fieldKey === 'lastName') {
        currentValue = lastName;
      } else if (fieldKey === 'birthday') {
        currentValue = birthday;
      } else if (fieldKey === 'gender') {
        currentValue = gender;
      } else if (fieldKey === 'ethnicity') {
        currentValue = ethnicity;
      } else if (fieldKey === 'languages') {
        currentValue = languages;
      }
      
      if (currentValue !== null) {
        onFieldSaved(fieldKey, currentValue);
      }
    } catch (error) {
      console.error(`Error autosaving ${fieldKey}:`, error);
      // Revert to last saved value
      applyRevertFn();
      toast({
        title: `Couldn't save ${fieldDisplayName}`,
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSavingFlag(false);
    }
  };

  const handlePronounToggle = (pronounValue: string) => {
    const isSelected = pronouns.some((p) => p.value === pronounValue);
    const updatedPronouns = isSelected
      ? pronouns.filter((p) => p.value !== pronounValue)
      : (() => {
          const pronounOption = pronounOptions.find((p) => p.value === pronounValue);
          return pronounOption ? [...pronouns, pronounOption] : pronouns;
        })();
    
    setPronouns(updatedPronouns);
    
    // Autosave pronouns
    if (lastSavedPersonalInfo) {
      const lastSavedPronounValues = lastSavedPersonalInfo.pronouns.map(p => p.value).sort().join(',');
      const currentPronounValues = updatedPronouns.map(p => p.value).sort().join(',');
      if (lastSavedPronounValues !== currentPronounValues) {
        autosaveField(
          'pronouns',
          () => ({ pronouns: updatedPronouns.map((p) => p.value) }),
          setIsSavingPronouns,
          () => setPronouns(lastSavedPersonalInfo.pronouns),
          'Pronouns'
        );
      }
    }
  };

  const isPronounSelected = (pronounValue: string) => {
    return pronouns.some((p) => p.value === pronounValue);
  };

  const handleAddLanguage = (language: string) => {
    if (language && !languages.includes(language)) {
      const updatedLanguages = [...languages, language];
      setLanguages(updatedLanguages);
      setShowLanguageInput(false);
      setLanguageInput('');
      
      // Autosave languages
      if (lastSavedPersonalInfo) {
        const lastSavedLanguagesStr = [...lastSavedPersonalInfo.languages].sort().join(',');
        const currentLanguagesStr = [...updatedLanguages].sort().join(',');
        if (lastSavedLanguagesStr !== currentLanguagesStr) {
          autosaveField(
            'languages',
            () => ({ languages: updatedLanguages }),
            setIsSavingLanguages,
            () => setLanguages(lastSavedPersonalInfo.languages),
            'Languages'
          );
        }
      }
    } else {
      setShowLanguageInput(false);
      setLanguageInput('');
    }
  };

  const handleRemoveLanguage = (language: string) => {
    const updatedLanguages = languages.filter((l) => l !== language);
    setLanguages(updatedLanguages);
    
    // Autosave languages
    if (lastSavedPersonalInfo) {
      const lastSavedLanguagesStr = [...lastSavedPersonalInfo.languages].sort().join(',');
      const currentLanguagesStr = [...updatedLanguages].sort().join(',');
      if (lastSavedLanguagesStr !== currentLanguagesStr) {
        autosaveField(
          'languages',
          () => ({ languages: updatedLanguages }),
          setIsSavingLanguages,
          () => setLanguages(lastSavedPersonalInfo.languages),
          'Languages'
        );
      }
    }
  };

  return (
      <VStack spacing={6} align="stretch">
        {/* First Name */}
        <FormControl>
          <FormLabel>
            First name
          </FormLabel>
          <InputGroup>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => {
                if (lastSavedPersonalInfo && firstName !== lastSavedPersonalInfo.firstName) {
                  autosaveField(
                    'firstName',
                    () => ({ firstName }),
                    setIsSavingFirstName,
                    () => setFirstName(lastSavedPersonalInfo.firstName),
                    'First Name'
                  );
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (lastSavedPersonalInfo && firstName !== lastSavedPersonalInfo.firstName) {
                    autosaveField(
                      'firstName',
                      () => ({ firstName }),
                      setIsSavingFirstName,
                      () => setFirstName(lastSavedPersonalInfo.firstName),
                      'First Name'
                    );
                  }
                }
              }}
              placeholder="Enter your first name"
              pr={isSavingFirstName ? '3rem' : undefined}
            />
            {isSavingFirstName && (
              <InputRightElement width="3rem">
                <Spinner size="sm" color={ff.muted} />
              </InputRightElement>
            )}
          </InputGroup>
        </FormControl>

        {/* Last Name */}
        <FormControl>
          <FormLabel>
            Last name
          </FormLabel>
          <InputGroup>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => {
                if (lastSavedPersonalInfo && lastName !== lastSavedPersonalInfo.lastName) {
                  autosaveField(
                    'lastName',
                    () => ({ lastName }),
                    setIsSavingLastName,
                    () => setLastName(lastSavedPersonalInfo.lastName),
                    'Last Name'
                  );
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (lastSavedPersonalInfo && lastName !== lastSavedPersonalInfo.lastName) {
                    autosaveField(
                      'lastName',
                      () => ({ lastName }),
                      setIsSavingLastName,
                      () => setLastName(lastSavedPersonalInfo.lastName),
                      'Last Name'
                    );
                  }
                }
              }}
              placeholder="Enter your last name"
              pr={isSavingLastName ? '3rem' : undefined}
            />
            {isSavingLastName && (
              <InputRightElement width="3rem">
                <Spinner size="sm" color={ff.muted} />
              </InputRightElement>
            )}
          </InputGroup>
        </FormControl>

        {/* Email Address */}
        <FormControl>
          <FormLabel>
            Email address
          </FormLabel>
          {/* Not autosaved on blur like the fields above: the address only changes once the
              creator has proven the new one by clicking the link we send there. */}
          <Input
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="email@example.com"
            isDisabled={isRequestingEmailChange || !!pendingEmail || !canChangeEmail}
          />

          {email_address && (
            <Text mt={2} fontSize="14px" color={ff.text2}>
              Current: <Text as="span" fontWeight={600} color={ff.ink}>{email_address}</Text>
            </Text>
          )}

          {pendingEmail ? (
            <Box mt={3} p={4} bg={ff.grey} borderRadius="20px">
              {pendingEmailCancelled ? (
                <>
                  <Text fontSize="14px" fontWeight={600} color={ff.ink}>
                    Change to {pendingEmail} cancelled
                  </Text>
                  {/* Kept on screen rather than cleared: the link is still out there, the address
                      is still reserved, and a new one is refused until this expires. Hiding it
                      left an enabled field that rejected everything typed into it. */}
                  <Text fontSize="13px" color={ff.body} mt={1}>
                    We can't take the link back, so opening it will still move your address. Until
                    it expires, that address stays reserved and you can't start another change.
                  </Text>
                </>
              ) : (
                <>
                  <Text fontSize="14px" fontWeight={600} color={ff.ink}>
                    Check your inbox at {pendingEmail}
                  </Text>
                  <Text fontSize="13px" color={ff.body} mt={1}>
                    Nothing changes until you open the link we sent there. Once you do, that address
                    becomes where brands reach you and how you sign in with email, and you'll need to
                    sign in again.
                  </Text>
                  <Text fontSize="12px" color={ff.text2} mt={1}>
                    Cancelling stops the change here, but a link we already sent will still work if
                    you open it, and the address stays reserved until it expires.
                  </Text>
                  <Button mt={3} variant="inkOutline" h="44px" px={5} fontSize="14px" onClick={onCancelEmailChange}>
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          ) : !canChangeEmail ? (
            <Text mt={2} fontSize="12px" color={ff.text2}>
              Switch back to your own account to change an email address. The link would move your
              login, not the creator's.
            </Text>
          ) : (
            <>
              <Button
                mt={3}
                variant="ink"
                h="44px"
                px={5}
                isLoading={isRequestingEmailChange}
                isDisabled={
                  !newEmail.trim()
                  || newEmail.trim().toLowerCase() === email_address?.toLowerCase()
                }
                onClick={onRequestEmailChange}
              >
                Change email
              </Button>
              <Text mt={2} fontSize="12px" color={ff.text2}>
                We'll send a link to the new address. Your current address keeps working until you
                open it.
              </Text>
            </>
          )}
        </FormControl>

        {/* Phone Number */}
        <FormControl>
          <FormLabel>
            Phone number
          </FormLabel>
          <Box position="relative">
            <Box
              sx={{
                '.PhoneInput': {
                  bg: 'white',
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: ff.border,
                  h: '48px',
                  px: 4,
                  pr: isSavingPhone ? '3rem' : 4,
                },
                '.PhoneInputInput': {
                    width: '100%',
                    height: '46px',
                    fontSize: '16px',
                    border: 'none',
                    background: 'transparent',
                  outline: 'none',
                  paddingLeft: '8px',
                },
                '.PhoneInputCountry': {
                  marginRight: '8px',
                },
                '.PhoneInput:focus-within': {
                  borderColor: ff.ink,
                  boxShadow: `0 0 0 1px ${ff.ink}`,
                },
              }}
            >
              <PhoneInput
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={(value) => setPhoneNumber(value || '')}
                onBlur={() => {
                  if (lastSavedPersonalInfo) {
                    const lastSavedPhone = lastSavedPersonalInfo.phoneNumber || '';
                    const currentPhone = formatPhoneNumber(phoneNumber, countryCode);
                    if (lastSavedPhone !== currentPhone) {
                      autosaveField(
                        'phoneNumber',
                        () => ({ phoneNumber: formatPhoneNumber(phoneNumber, countryCode) }),
                        setIsSavingPhone,
                        () => {
                          const parsed = parsePhone(lastSavedPersonalInfo.phoneNumber);
                          setPhoneNumber(parsed.nationalNumber);
                          setCountryCode(parsed.countryCode);
                        },
                        'Phone Number'
                      );
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (lastSavedPersonalInfo && phoneNumber !== lastSavedPersonalInfo.phoneNumber) {
                      autosaveField(
                        'phoneNumber',
                        () => ({ phoneNumber: formatPhoneNumber(phoneNumber, countryCode) }),
                        setIsSavingPhone,
                        () => {
                          const parsed = parsePhone(lastSavedPersonalInfo.phoneNumber);
                          setPhoneNumber(parsed.nationalNumber);
                          setCountryCode(parsed.countryCode);
                        },
                        'Phone Number'
                      );
                    }
                  }
                }}
                defaultCountry="US"
                international
                />
            </Box>
            {isSavingPhone && (
              <Box
                position="absolute"
                right="1rem"
                top="50%"
                transform="translateY(-50%)"
                zIndex={1}
              >
                <Spinner size="sm" color={ff.muted} />
              </Box>
            )}
          </Box>
        </FormControl>

        {/* Birthday */}
        <FormControl>
          <FormLabel>
            Birthday
          </FormLabel>
          <InputGroup>
            <Input
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              onBlur={() => {
                if (lastSavedPersonalInfo && birthday !== lastSavedPersonalInfo.birthday) {
                  autosaveField(
                    'birthday',
                    () => ({ birthday }),
                    setIsSavingBirthday,
                    () => setBirthday(lastSavedPersonalInfo.birthday),
                    'Birthday'
                  );
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (lastSavedPersonalInfo && birthday !== lastSavedPersonalInfo.birthday) {
                    autosaveField(
                      'birthday',
                      () => ({ birthday }),
                      setIsSavingBirthday,
                      () => setBirthday(lastSavedPersonalInfo.birthday),
                      'Birthday'
                    );
                  }
                }
              }}
              pr={isSavingBirthday ? '3rem' : undefined}
            />
            {isSavingBirthday && (
              <InputRightElement width="3rem">
                <Spinner size="sm" color={ff.muted} />
              </InputRightElement>
            )}
          </InputGroup>
        </FormControl>

        {/* Gender */}
        <FormControl>
          <FormLabel>
            Gender
            {isSavingGender && (
              <Spinner size="sm" color={ff.muted} ml={2} />
            )}
          </FormLabel>
          <Select
            value={gender}
            onChange={(e) => {
              const newValue = e.target.value;
              setGender(newValue);
              if (lastSavedPersonalInfo && newValue !== lastSavedPersonalInfo.gender) {
                autosaveField(
                  'gender',
                  () => ({ gender: newValue }),
                  setIsSavingGender,
                  () => setGender(lastSavedPersonalInfo.gender),
                  'Gender'
                );
              }
            }}
            placeholder="Select gender"
            isDisabled={isSavingGender}
          >
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Non-binary">Non-binary</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </Select>
        </FormControl>

        {/* Pronouns */}
        <FormControl>
          <FormLabel>
          Pronouns (select all that apply)
          {isSavingPronouns && (
            <Spinner size="sm" color={ff.muted} ml={2} />
          )}
          </FormLabel>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={3}>
          {pronounOptions.map((option) => {
            const isSelected = isPronounSelected(option.value);
            return (
              <Box
                key={option.value}
                position="relative"
                bg={isSelected ? ff.ink : 'white'}
                color={isSelected ? ff.lime : ff.ink}
                border="1px solid"
                borderColor={isSelected ? ff.ink : ff.border}
                borderRadius="full"
                px={4}
                minH="48px"
                display="flex"
                alignItems="center"
                cursor={isSavingPronouns ? 'not-allowed' : 'pointer'}
                opacity={isSavingPronouns ? 0.6 : 1}
                transition="all 0.2s"
                _hover={{ borderColor: ff.ink }}
                onClick={() => !isSavingPronouns && handlePronounToggle(option.value)}
              >
                {isSelected && (
                  <Flex
                    position="absolute"
                    top="50%"
                    right={3}
                    transform="translateY(-50%)"
                    bg={ff.lime}
                    borderRadius="full"
                    w="20px"
                    h="20px"
                    align="center"
                    justify="center"
                  >
                    {isSavingPronouns ? (
                      <Spinner size="xs" color={ff.ink} />
                    ) : (
                      <CheckIcon color={ff.ink} boxSize={2.5} />
                    )}
                  </Flex>
                )}
                <Text fontSize="14px" fontWeight={600} pr={isSelected ? 6 : 0}>
                  {option.label}
                </Text>
              </Box>
            );
          })}
        </Box>
        </FormControl>

        {/* Ethnicity */}
        <FormControl>
          <FormLabel>
            Ethnicity
            {isSavingEthnicity && (
              <Spinner size="sm" color={ff.muted} ml={2} />
            )}
          </FormLabel>
          <Select
          placeholder="Select ethnicity"
            value={ethnicity}
            onChange={(e) => {
              const newValue = e.target.value;
              setEthnicity(newValue);
              if (lastSavedPersonalInfo && newValue !== lastSavedPersonalInfo.ethnicity) {
                autosaveField(
                  'ethnicity',
                  () => ({ ethnicity: newValue }),
                  setIsSavingEthnicity,
                  () => setEthnicity(lastSavedPersonalInfo.ethnicity),
                  'Ethnicity'
                );
              }
            }}
          isDisabled={isSavingEthnicity}
        >
          {ethnicityOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FormControl>

      {/* Languages */}
      <FormControl>
        <FormLabel>
          Languages
          {isSavingLanguages && (
            <Spinner size="sm" color={ff.muted} ml={2} />
          )}
        </FormLabel>

        {/* Selected Languages */}
        {languages.length > 0 && (
          <Flex flexWrap="wrap" gap={2} mb={3}>
            {languages.map((language) => (
              <Flex
                key={language}
                align="center"
                bg={ff.ink}
                color={ff.lime}
                borderRadius="full"
                pl={4}
                pr={1}
                h="44px"
                fontSize="14px"
                fontWeight={600}
              >
                <Text>{language}</Text>
                <IconButton
                  icon={isSavingLanguages ? <Spinner size="xs" /> : <Box as="span" fontSize="18px">×</Box>}
                  variant="unstyled"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color={ff.lime}
                  aria-label={`Remove ${language}`}
                  onClick={() => !isSavingLanguages && handleRemoveLanguage(language)}
                  minW="36px"
                  w="36px"
                  h="36px"
                  borderRadius="full"
                  isDisabled={isSavingLanguages}
                />
              </Flex>
            ))}
          </Flex>
        )}

        {/* Add Language Input */}
        {!showLanguageInput ? (
          <Box
            border="1px dashed"
            borderColor={ff.border}
            borderRadius="14px"
            px={4}
            h="48px"
            display="flex"
            alignItems="center"
            cursor={isSavingLanguages ? 'not-allowed' : 'pointer'}
            bg="white"
            opacity={isSavingLanguages ? 0.6 : 1}
            _hover={{ borderColor: ff.ink }}
            onClick={() => !isSavingLanguages && setShowLanguageInput(true)}
          >
            <Flex align="center" justify="space-between" w="100%">
              <Text fontSize="14px" color={ff.text2}>
                Add a language
              </Text>
              {isSavingLanguages && (
                <Spinner size="sm" color={ff.muted} />
              )}
            </Flex>
          </Box>
        ) : (
          <Select
            placeholder="Select language"
            value={languageInput}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                handleAddLanguage(value);
              } else {
                setShowLanguageInput(false);
              }
            }}
            autoFocus
            isDisabled={isSavingLanguages}
          >
            {languageOptions
              .filter(lang => !languages.includes(lang))
              .map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
          </Select>
        )}
        </FormControl>
      </VStack>
  );
};

export default PersonalInformationEdit;